import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CalendarPlus, CalendarX } from "lucide-react";
import {
  ScheduleInput,
  type ScheduleEntryValue,
} from "@/components/schedule-input/schedule-input";
import {
  Schedule,
  calculateDurationMinutes,
} from "@/features/job-offer/utils/job-offer-data";
import { ScheduleType } from "@api/shared-runtime/enums";
import { useTRPC } from "@/trpc";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { format, parseISO, eachDayOfInterval, parse } from "date-fns";
import { type RRuleOptions, buildRRule } from "@/utils/rrule-helpers";
import { toast } from "sonner";

export type ShiftEntry = {
  id: string;
  startsAt: Date | string;
  endsAt: Date | string;
  status: string;
};

interface EditDatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobOfferId: string;
  shifts?: ShiftEntry[];
  /** Pre-select these shift IDs for cancellation (e.g. from the manage shifts trash icon) */
  initialCancelShiftIds?: string[];
}

export const EditDatesDialog: React.FC<EditDatesDialogProps> = ({
  open,
  onOpenChange,
  jobOfferId,
  shifts,
  initialCancelShiftIds,
}) => {
  const trpc = useTRPC();
  const [scheduleEntries, setScheduleEntries] = useState<ScheduleEntryValue[]>(
    [],
  );
  const [cancelShiftIds, setCancelShiftIds] = useState<Set<string>>(
    new Set(initialCancelShiftIds),
  );
  const [note, setNote] = useState("");

  // Reset state when dialog opens with new initial values
  React.useEffect(() => {
    if (open) {
      setCancelShiftIds(new Set(initialCancelShiftIds));
      setScheduleEntries([]);
      setNote("");
    }
  }, [open, initialCancelShiftIds]);

  const cancelableShifts = React.useMemo(() => {
    if (!shifts) {
      return [];
    }
    const now = new Date();
    return shifts
      .filter((s) => {
        if (s.status === "canceled") {
          return false;
        }
        const startsAt =
          typeof s.startsAt === "string" ? parseISO(s.startsAt) : s.startsAt;
        return startsAt > now;
      })
      .sort((a, b) => {
        const dateA =
          typeof a.startsAt === "string" ? new Date(a.startsAt) : a.startsAt;
        const dateB =
          typeof b.startsAt === "string" ? new Date(b.startsAt) : b.startsAt;
        return dateA.getTime() - dateB.getTime();
      });
  }, [shifts]);

  const toggleShiftCancel = (shiftId: string) => {
    setCancelShiftIds((prev) => {
      const next = new Set(prev);
      if (next.has(shiftId)) {
        next.delete(shiftId);
      } else {
        next.add(shiftId);
      }
      return next;
    });
  };

  const convertToApiSchedules = (entries: ScheduleEntryValue[]): Schedule[] => {
    const schedules: Schedule[] = [];

    for (const entry of entries) {
      if (!entry.startDate || !entry.startTime || !entry.endTime) {
        continue;
      }

      const durationMinutes = calculateDurationMinutes(
        entry.startTime,
        entry.endTime,
      );

      if (entry.recurrence.enabled) {
        const startsAt = `${entry.startDate}T${entry.startTime}:00`;

        let actualCount: number | undefined;
        if (entry.recurrence.endType === "count") {
          if (
            entry.recurrence.freq === "WEEKLY" &&
            entry.recurrence.byDay &&
            entry.recurrence.byDay.length > 1
          ) {
            actualCount =
              entry.recurrence.count * entry.recurrence.byDay.length;
          } else {
            actualCount = entry.recurrence.count;
          }
        }

        const rruleOptions: RRuleOptions = {
          freq: entry.recurrence.freq,
          interval: entry.recurrence.interval,
          byDay:
            entry.recurrence.freq === "WEEKLY"
              ? entry.recurrence.byDay
              : undefined,
          count: actualCount,
          until:
            entry.recurrence.endType === "until" && entry.recurrence.until
              ? parse(entry.recurrence.until, "yyyy-MM-dd", new Date())
              : undefined,
        };

        schedules.push({
          startsAt,
          durationMinutes,
          rrule: buildRRule(rruleOptions),
          scheduleType: entry.isOnCall
            ? ScheduleType.OnCall
            : ScheduleType.Regular,
        });
      } else {
        const startDate = parseISO(entry.startDate);
        const endDate = entry.endDate ? parseISO(entry.endDate) : startDate;

        const days = eachDayOfInterval({ start: startDate, end: endDate });

        for (const day of days) {
          const startsAt = `${format(day, "yyyy-MM-dd")}T${entry.startTime}:00`;
          schedules.push({
            startsAt,
            durationMinutes,
            rrule: null,
            scheduleType: entry.isOnCall
              ? ScheduleType.OnCall
              : ScheduleType.Regular,
          });
        }
      }
    }

    return schedules;
  };

  const { mutateAsync: createProposal, isPending } = useMutation(
    trpc.jobOffer.createProposal.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.jobOffer.organizationGetAll.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.jobOffer.organizationGet.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.jobOffer.orgGet.queryKey(),
          }),
        ]);
        onOpenChange(false);
        toast.success("Proposal sent to the provider");
      },
    }),
  );

  const handleSubmit = async () => {
    const apiSchedules = convertToApiSchedules(scheduleEntries);
    const shiftIdsToCancel = Array.from(cancelShiftIds);

    if (apiSchedules.length === 0 && shiftIdsToCancel.length === 0) {
      return;
    }

    await createProposal({
      jobOfferId,
      schedules: apiSchedules.length > 0 ? apiSchedules : undefined,
      shiftIds: shiftIdsToCancel.length > 0 ? shiftIdsToCancel : undefined,
      initiatedNote: note || undefined,
    });
  };

  const hasValidSchedules =
    scheduleEntries.length > 0 &&
    scheduleEntries.some(
      (entry) => entry.startDate && entry.startTime && entry.endTime,
    );

  const hasChanges = hasValidSchedules || cancelShiftIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Propose Date Changes</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Cancel shifts section */}
          {cancelableShifts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CalendarX className="h-4 w-4 text-rose-500" />
                <Label className="text-sm font-semibold">Cancel shifts</Label>
              </div>
              <p className="text-xs text-gray-500">
                Select shifts to cancel. The provider will need to accept the
                proposal before changes take effect.
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {cancelableShifts.map((shift) => {
                  const startsAt =
                    typeof shift.startsAt === "string"
                      ? parseISO(shift.startsAt)
                      : shift.startsAt;
                  const endsAt =
                    typeof shift.endsAt === "string"
                      ? parseISO(shift.endsAt)
                      : shift.endsAt;
                  const isSelected = cancelShiftIds.has(shift.id);
                  return (
                    <label
                      key={shift.id}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                        isSelected
                          ? "border-rose-200 bg-rose-50 text-rose-700"
                          : "border-gray-100 bg-gray-50 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleShiftCancel(shift.id)}
                      />
                      <span>
                        {format(startsAt, "MMM d, yyyy")} &middot;{" "}
                        {format(startsAt, "h:mm a")} &ndash;{" "}
                        {format(endsAt, "h:mm a")}
                      </span>
                    </label>
                  );
                })}
              </div>
              {cancelShiftIds.size > 0 && (
                <p className="text-xs font-medium text-rose-600">
                  {cancelShiftIds.size} shift
                  {cancelShiftIds.size !== 1 ? "s" : ""} selected for
                  cancellation
                </p>
              )}
            </div>
          )}

          {/* Add dates section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CalendarPlus className="h-4 w-4 text-emerald-500" />
              <Label className="text-sm font-semibold">Add dates</Label>
            </div>
            <ScheduleInput
              value={scheduleEntries}
              onChange={setScheduleEntries}
            />
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="proposal-note">Note (optional)</Label>
            <Textarea
              id="proposal-note"
              placeholder="Add a note for the provider..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasChanges || isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Proposing...
              </>
            ) : (
              "Propose Date Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
